import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Select,
  Stack,
  FormControl,
  FormHelperText,
  InputLabel,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import { DatePicker } from "@mui/x-date-pickers";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createManualBooking, createManualItineraryBooking } from "../../api/bookings";
import { searchCities, searchTrips, getTopCities } from "../../api";
import TripList from "../../components/TripList";
import SkeletonTripList from "../../components/SkeletonTripList";
import dayjs from "dayjs";
import { getCityDisplay } from "../../utils/city";
import { storeBookingAccess } from "../../utils/bookingAccess";
import { getOrganizations } from "../../api/organizations";
import { getAgents } from "../../api/agents";
import { isDefaultCompanyUser } from "../../utils/auth";

const capitalizeName = (str) => {
  if (!str || typeof str !== "string") return str;
  return str.trim().replace(/\S+/g, (word) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
};

const capitalizeNameWhileTyping = (str) => {
  if (!str || typeof str !== "string") return str;
  return str.replace(/\S+/g, (word) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
};

const cityLabel = (city, lang) =>
  city && typeof city === "object"
    ? getCityDisplay(city, lang)
    : String(city ?? "");

const MAX_PASSENGER_COUNT = 99;

const parsePassengerCount = (raw, min) => {
  const n = Number.parseInt(String(raw), 10);
  if (!Number.isFinite(n)) return min;
  return Math.min(MAX_PASSENGER_COUNT, Math.max(min, n));
};

const acceptPassengerCountDraft = (raw) => raw === "" || /^\d{1,2}$/.test(raw);

const CompanyAdminBookingCreate = () => {
  const { t, i18n } = useTranslation("common");
  const lang = i18n.language?.startsWith("en") ? "en" : "uk";
  const navigate = useNavigate();

  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const [searchDate, setSearchDate] = useState(dayjs());
  const [fromCityOptions, setFromCityOptions] = useState([]);
  const [toCityOptions, setToCityOptions] = useState([]);
  const [topFromCities, setTopFromCities] = useState([]);
  const [topToCities, setTopToCities] = useState([]);
  const [loadingFrom, setLoadingFrom] = useState(false);
  const [loadingTo, setLoadingTo] = useState(false);

  const [searching, setSearching] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [directTrips, setDirectTrips] = useState([]);
  const [transfers, setTransfers] = useState([]);

  /** { type: 'direct'|'transfer', trip?, itinerary?, fromCity, toCity } */
  const [selection, setSelection] = useState(null);

  const [adults, setAdults] = useState("1");
  const [children, setChildren] = useState("0");
  const adultCount = parsePassengerCount(adults, 1);
  const childCount = parsePassengerCount(children, 0);
  const [passengerData, setPassengerData] = useState([]);
  const [passengerErrors, setPassengerErrors] = useState([]);
  const [buyerPhone, setBuyerPhone] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("not_paid");
  const [fundsHolder, setFundsHolder] = useState("sv");
  const [agent, setAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [ticketLinkDialog, setTicketLinkDialog] = useState({ open: false, link: "" });
  const [linkCopied, setLinkCopied] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyPage, setCompanyPage] = useState(1);
  const [hasMoreCompanies, setHasMoreCompanies] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const COMPANY_PAGE_SIZE = 20;

  useEffect(() => {
    getAgents({ assignable: 1 })
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : rows?.results || [];
        setAgents(list);
      })
      .catch(() => setAgents([]));
  }, []);

  const fetchCompanies = async (page = 1) => {
    if (loadingCompanies) return;
    if (page > 1 && !hasMoreCompanies) return;
    setLoadingCompanies(true);
    try {
      const res = await getOrganizations(page, COMPANY_PAGE_SIZE);
      const results = res.data.results || res.data;
      setCompanies((prev) => (page === 1 ? results : [...prev, ...results]));
      setHasMoreCompanies(!!res.data.next);
      setCompanyPage(page + 1);
    } catch (e) {
      console.error("Помилка при завантаженні компаній", e);
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    fetchCompanies(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getTopCities(lang, 5, "from"), getTopCities(lang, 5, "to")])
      .then(([fromData, toData]) => {
        if (cancelled) return;
        const fromList = Array.isArray(fromData) ? fromData : [];
        const toList = Array.isArray(toData) ? toData : [];
        setTopFromCities(fromList);
        setTopToCities(toList);
        setFromCityOptions(fromList);
        setToCityOptions(toList);
      })
      .catch(() => {
        if (cancelled) return;
        setTopFromCities([]);
        setTopToCities([]);
        setFromCityOptions([]);
        setToCityOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  useEffect(() => {
    const total = adultCount + childCount;
    setPassengerData(
      Array.from({ length: total }).map((_, i) => ({
        name: "",
        surname: "",
        type: i < adultCount ? "adult" : "child",
      }))
    );
    setPassengerErrors(
      Array.from({ length: total }).map(() => ({ name: false, surname: false }))
    );
  }, [adultCount, childCount]);

  const fetchCities = async (input, setOptions, setLoadingState, topList) => {
    if (input.length < 2) {
      setOptions(topList);
      return;
    }
    setLoadingState(true);
    try {
      const response = await searchCities(input, lang);
      setOptions(Array.isArray(response) ? response : []);
    } catch {
      setOptions([]);
    } finally {
      setLoadingState(false);
    }
  };

  const handleSearch = async () => {
    if (!searchFrom?.trim() || !searchTo?.trim() || !searchDate) return;
    setSearching(true);
    setSelection(null);
    setSearchDone(false);
    setDisabled(false);
    try {
      const result = await searchTrips(
        searchFrom.trim(),
        searchTo.trim(),
        searchDate.format("YYYY-MM-DD"),
        adultCount,
        childCount,
        lang
      );
      setDirectTrips(Array.isArray(result?.direct) ? result.direct : []);
      setTransfers(Array.isArray(result?.transfers) ? result.transfers : []);
    } catch {
      setDirectTrips([]);
      setTransfers([]);
      setSnackbar({
        open: true,
        message: t("admin.createError"),
        severity: "error",
      });
    } finally {
      setSearching(false);
      setSearchDone(true);
    }
  };

  const handleSelectTrip = (payload) => {
    if (payload?.type === "transfer" || payload?.itinerary) {
      setSelection({
        type: "transfer",
        itinerary: payload.itinerary,
        fromCity: payload.fromCity ?? searchFrom,
        toCity: payload.toCity ?? searchTo,
      });
      return;
    }
    setSelection({
      type: "direct",
      trip: payload.trip,
      fromCity: payload.fromCity ?? searchFrom,
      toCity: payload.toCity ?? searchTo,
    });
  };

  const passengersValid = passengerData.every(
    (p) => p.name.trim().length > 1 && p.surname.trim().length > 1
  );
  const phoneValid = matchIsValidTel(buyerPhone);
  const isFormValid = !!selection && passengersValid && phoneValid;

  const validatePassengerField = (index, field) => {
    const value = passengerData[index][field].trim();
    const updatedErrors = [...passengerErrors];
    updatedErrors[index][field] = value.length < 2;
    setPassengerErrors(updatedErrors);
  };

  const resetPassengerError = (index, field) => {
    const updatedErrors = [...passengerErrors];
    updatedErrors[index][field] = false;
    setPassengerErrors(updatedErrors);
  };

  const handleChangePassenger = (index, field, value) => {
    const updated = [...passengerData];
    const isNameField = field === "name" || field === "surname";
    updated[index][field] = isNameField ? capitalizeNameWhileTyping(value) : value;
    setPassengerData(updated);
  };

  const handlePassengerNameBlur = (index, field) => {
    validatePassengerField(index, field);
    const value = passengerData[index][field];
    const capitalized = capitalizeName(value);
    if (capitalized !== value) {
      const updated = [...passengerData];
      updated[index][field] = capitalized;
      setPassengerData(updated);
    }
  };

  const handlePhoneBlur = () => setPhoneError(!matchIsValidTel(buyerPhone));
  const handleEmailBlur = () =>
    setEmailError(buyerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyerEmail));

  const generateTicketLink = (bookingId, accessToken) => {
    const base = `${window.location.origin}/booking/${bookingId}`;
    return accessToken ? `${base}?access=${encodeURIComponent(accessToken)}` : base;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(ticketLinkDialog.link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const extractErrorMessage = (err) => {
    let errorMessage = t("admin.createError");
    if (err?.response?.data) {
      const data = err.response.data;
      if (typeof data === "string") errorMessage = data;
      else if (typeof data.error === "string") errorMessage = data.error;
      else if (typeof data.detail === "string") errorMessage = data.detail;
      else if (typeof data === "object") {
        const firstKey = Object.keys(data)[0];
        const firstError = data[firstKey];
        errorMessage = Array.isArray(firstError) ? firstError[0] : String(firstError);
      }
    } else if (err?.message) {
      errorMessage = err.message;
    }
    return errorMessage;
  };

  const cityNameForApi = (value) => {
    if (!value) return "";
    if (typeof value === "object") return value.name || getCityDisplay(value, "uk");
    return String(value);
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;

    const normalizedPassengers = passengerData.map((p) => ({
      ...p,
      name: capitalizeName(p.name),
      surname: capitalizeName(p.surname),
    }));

    setLoading(true);
    try {
      let response;
      if (selection.type === "transfer") {
        const bookingData = {
          legs: selection.itinerary.legs.map((leg) => ({
            trip_id: leg.trip.id,
            from_city: leg.from_city?.name ?? leg.from_city,
            to_city: leg.to_city?.name ?? leg.to_city,
          })),
          passengers: normalizedPassengers,
          buyer_email: buyerEmail || undefined,
          buyer_phone: buyerPhone.replace(/\s+/g, ""),
          status: paymentStatus,
          funds_holder: fundsHolder,
        };
        if (selectedCompany) bookingData.organization_id = selectedCompany;
        if (agent?.id) bookingData.selling_organization_id = agent.id;
        response = await createManualItineraryBooking(bookingData);
      } else {
        const bookingData = {
          trip_id: selection.trip.id,
          from_city: cityNameForApi(selection.fromCity),
          to_city: cityNameForApi(selection.toCity),
          adults: adultCount,
          children: childCount,
          passengers: normalizedPassengers,
          buyer_email: buyerEmail,
          buyer_phone: buyerPhone.replace(/\s+/g, ""),
          status: paymentStatus,
          funds_holder: fundsHolder,
        };
        if (selectedCompany) bookingData.organization_id = selectedCompany;
        if (agent?.id) bookingData.selling_organization_id = agent.id;
        response = await createManualBooking(bookingData);
      }

      const bookingId = response?.booking_id;
      if (bookingId) {
        if (response?.access_token) {
          storeBookingAccess(bookingId, response.access_token);
        }
        setTicketLinkDialog({
          open: true,
          link: generateTicketLink(bookingId, response?.access_token),
        });
      }
      setSnackbar({ open: true, message: t("admin.bookingCreated"), severity: "success" });
      setDisabled(true);
    } catch (err) {
      setSnackbar({ open: true, message: extractErrorMessage(err), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const selectionLabel =
    selection?.type === "transfer"
      ? t("admin.transferSelected", {
          from: searchFrom,
          to: searchTo,
          hub: getCityDisplay(selection.itinerary.hub_city, lang),
        })
      : selection
        ? t("admin.selectedTrip", { from: searchFrom, to: searchTo })
        : "";

  return (
    <Box sx={{ maxWidth: "900px", mx: "auto", mt: 2, mb: 5, px: 2 }}>
      <Box
        sx={{
          position: "relative",
          mb: 3,
          minHeight: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          variant="outlined"
          sx={{
            position: { xs: "static", sm: "absolute" },
            left: { sm: 0 },
            top: { sm: "50%" },
            transform: { sm: "translateY(-50%)" },
            alignSelf: { xs: "flex-start", sm: "auto" },
          }}
        >
          {t("newBookingForm.back")}
        </Button>
        <Typography
          variant="h4"
          sx={{ fontWeight: 600, textAlign: "center", width: "100%" }}
        >
          {t("newBookingForm.title")}
        </Typography>
      </Box>

      <Card sx={{ boxShadow: 1, borderRadius: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={fromCityOptions}
                getOptionLabel={(city) => cityLabel(city, lang)}
                inputValue={searchFrom}
                onInputChange={(_e, value) => {
                  setSearchFrom(value);
                  fetchCities(value, setFromCityOptions, setLoadingFrom, topFromCities);
                }}
                onChange={(_e, value) => {
                  setSearchFrom(
                    value && typeof value === "object"
                      ? getCityDisplay(value, lang)
                      : value || ""
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.fromCity")}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingFrom && <CircularProgress size={18} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={toCityOptions}
                getOptionLabel={(city) => cityLabel(city, lang)}
                inputValue={searchTo}
                onInputChange={(_e, value) => {
                  setSearchTo(value);
                  fetchCities(value, setToCityOptions, setLoadingTo, topToCities);
                }}
                onChange={(_e, value) => {
                  setSearchTo(
                    value && typeof value === "object"
                      ? getCityDisplay(value, lang)
                      : value || ""
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.toCity")}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTo && <CircularProgress size={18} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label={t("admin.date")}
                value={searchDate}
                onChange={(d) => setSearchDate(d)}
                format="DD.MM.YYYY"
                minDate={dayjs()}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={t("search.adults")}
                type="text"
                fullWidth
                value={adults}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (acceptPassengerCountDraft(raw)) setAdults(raw);
                }}
                onBlur={() => setAdults((current) => String(parsePassengerCount(current, 1)))}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  enterKeyHint: "done",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={t("search.children")}
                type="text"
                fullWidth
                value={children}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (acceptPassengerCountDraft(raw)) setChildren(raw);
                }}
                onBlur={() =>
                  setChildren((current) => String(parsePassengerCount(current, 0)))
                }
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  enterKeyHint: "done",
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSearch}
                disabled={
                  searching ||
                  !searchFrom.trim() ||
                  !searchTo.trim() ||
                  !searchDate
                }
                sx={{ height: 56 }}
              >
                {searching ? t("admin.searchingTrips") : t("admin.findTrips")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {searching && (
        <Box sx={{ mt: 3 }}>
          <SkeletonTripList />
        </Box>
      )}

      {!searching && searchDone && !selection && (
        <Box sx={{ mt: 3 }}>
          <Typography fontWeight={600} sx={{ mb: 2 }}>
            {t("admin.selectTrip")}
          </Typography>
          <TripList
            directTrips={directTrips}
            transferItineraries={transfers}
            fromCity={searchFrom}
            toCity={searchTo}
            searchDate={searchDate}
            adults={adultCount}
            children={childCount}
            onSelectTrip={handleSelectTrip}
            hidePartners
          />
        </Box>
      )}

      {selection && (
        <Alert
          severity="success"
          sx={{ mt: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => setSelection(null)}>
              {t("admin.changeTrip")}
            </Button>
          }
        >
          {selectionLabel}
          {selection.type === "transfer"
            ? ` — ${t("units.uahAmount", { amount: Math.round(selection.itinerary.total_price_uah ?? 0) })}`
            : selection.trip?.fare?.price_uah != null
              ? ` — ${t("units.uahAmount", { amount: Math.round(selection.trip.fare.price_uah) })}`
              : ""}
        </Alert>
      )}

      {selection &&
        passengerData.map((p, i) => (
          <Card key={i} sx={{ mt: 3, boxShadow: 1, borderRadius: 3 }}>
            <CardContent>
              <Typography fontWeight={600} gutterBottom>
                {t("admin.passengerN", { n: i + 1 })} (
                {p.type === "adult" ? t("checkout.adult") : t("checkout.child")})
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("form.name")}
                    value={p.name}
                    onChange={(e) => handleChangePassenger(i, "name", e.target.value)}
                    onBlur={() => handlePassengerNameBlur(i, "name")}
                    onFocus={() => resetPassengerError(i, "name")}
                    fullWidth
                    error={passengerErrors[i]?.name}
                    helperText={
                      passengerErrors[i]?.name ? t("admin.nameMinLength") : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("form.surname")}
                    value={p.surname}
                    onChange={(e) => handleChangePassenger(i, "surname", e.target.value)}
                    onBlur={() => handlePassengerNameBlur(i, "surname")}
                    onFocus={() => resetPassengerError(i, "surname")}
                    fullWidth
                    error={passengerErrors[i]?.surname}
                    helperText={
                      passengerErrors[i]?.surname ? t("admin.surnameMinLength") : ""
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}

      {selection && (
        <>
          <Card sx={{ mt: 3, boxShadow: 1, borderRadius: 3 }}>
            <CardContent>
              <Typography fontWeight={600} gutterBottom>
                {t("checkout.buyerData")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MuiTelInput
                    label={t("checkout.yourPhone")}
                    value={buyerPhone}
                    onChange={setBuyerPhone}
                    defaultCountry="UA"
                    fullWidth
                    required
                    onBlur={handlePhoneBlur}
                    error={phoneError}
                    helperText={phoneError ? t("checkout.invalidPhone") : ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("checkout.yourEmail")}
                    fullWidth
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    onBlur={handleEmailBlur}
                    error={emailError}
                    helperText={emailError ? t("admin.invalidEmail") : ""}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3, boxShadow: 1, borderRadius: 3 }}>
            <CardContent>
              <Autocomplete
                options={agents}
                value={agent}
                onChange={(_event, value) => setAgent(value)}
                getOptionLabel={(row) => row.name || row.display_name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={t("admin.tripPassengerSourceEmpty")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.tripPassengerSource")}
                    helperText={t("admin.tripPassengerSourceHint")}
                  />
                )}
              />
            </CardContent>
          </Card>

          <Card sx={{ mt: 3, boxShadow: 1, borderRadius: 3 }}>
            <CardContent>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>{t("admin.paymentStatus")}</InputLabel>
                  <Select
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                    label={t("admin.paymentStatus")}
                  >
                    <MenuItem value="not_paid">{t("status.not_paid")}</MenuItem>
                    <MenuItem value="paid">{t("status.paid")}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>{t("admin.fundsHolder")}</InputLabel>
                  <Select
                    value={fundsHolder}
                    onChange={(e) => setFundsHolder(e.target.value)}
                    label={t("admin.fundsHolder")}
                  >
                    <MenuItem value="sv">{t("admin.fundsHolderSv")}</MenuItem>
                    <MenuItem value="agent">{t("admin.fundsHolderAgent")}</MenuItem>
                    <MenuItem value="carrier">{t("admin.fundsHolderCarrier")}</MenuItem>
                  </Select>
                  <FormHelperText>{t("admin.fundsHolderHint")}</FormHelperText>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>

          {isDefaultCompanyUser() && (
            <Card sx={{ mt: 3, boxShadow: 1, borderRadius: 3 }}>
              <CardContent>
                <FormControl fullWidth>
                  <InputLabel>{t("admin.selectCompany")}</InputLabel>
                  <Select
                    value={selectedCompany || ""}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    label={t("admin.selectCompany")}
                    MenuProps={{
                      PaperProps: { style: { maxHeight: 320, overflow: "hidden" } },
                      MenuListProps: {
                        style: { maxHeight: 300, overflow: "auto" },
                        onScroll: (e) => {
                          const el = e.currentTarget;
                          const bottom =
                            el.scrollHeight - el.scrollTop <= el.clientHeight + 80;
                          if (bottom && hasMoreCompanies && !loadingCompanies) {
                            fetchCompanies(companyPage);
                          }
                        },
                      },
                    }}
                  >
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                    {loadingCompanies && (
                      <MenuItem disabled>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                          }}
                        >
                          <CircularProgress size={20} />
                        </Box>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          )}

          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={loading || !isFormValid || disabled}
              sx={{
                maxWidth: { sm: 420 },
                display: "flex",
                mx: "auto",
                px: 4,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              {loading ? t("admin.creating") : t("admin.createBooking")}
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={ticketLinkDialog.open}
        onClose={() => {
          setTicketLinkDialog({ open: false, link: "" });
          setTimeout(() => navigate("/administrator/dashboard"), 500);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {t("newBookingForm.ticketLinkTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            {t("newBookingForm.ticketLinkBody")}
          </Typography>
          <TextField
            fullWidth
            value={ticketLinkDialog.link}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCopyLink}
                    edge="end"
                    color={linkCopied ? "success" : "default"}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {linkCopied && (
            <Typography
              variant="caption"
              color="success.main"
              sx={{ mt: 1, display: "block" }}
            >
              {t("newBookingForm.copied")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTicketLinkDialog({ open: false, link: "" });
              setTimeout(() => navigate("/administrator/dashboard"), 500);
            }}
            variant="contained"
          >
            {t("newBookingForm.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyAdminBookingCreate;
